'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import {
  type Team,
  TeamsApi,
  type TeamWithCountsDto,
} from '@up2stay/up2promo-api-client';
import {
  Button,
  Input,
  Label,
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  Textarea,
} from '@up2stay/up2promo-ui';
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { triggerTapHaptic } from '@/core/lib/dnd-haptics';

import { TEAM_COLOR_PRESETS } from './team-color-presets';

const teamFormSchema = z.object({
  name: z.string().trim().min(1).max(80),
  description: z.string().trim().max(500).optional().or(z.literal('')),
  color: z
    .string()
    .regex(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/)
    .optional()
    .or(z.literal('')),
});

type TeamFormData = z.infer<typeof teamFormSchema>;

interface TeamFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  /** When set, dialog is in "edit" mode. */
  team?: Team | TeamWithCountsDto | null;
  onSuccess: (team: Team) => void;
  dict: {
    title: string;
    description: string;
    nameLabel: string;
    namePlaceholder: string;
    descriptionLabel: string;
    descriptionPlaceholder: string;
    colorLabel: string;
    cancel: string;
    submitLabel: string;
    submittingLabel: string;
    successMessage: string;
    errorMessage: string;
    errorNameTaken: string;
  };
}

export function TeamFormDialog({
  open,
  onOpenChange,
  workspaceId,
  team,
  onSuccess,
  dict,
}: TeamFormDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isEdit = Boolean(team);

  const { control, ...form } = useForm<TeamFormData>({
    resolver: zodResolver(teamFormSchema as never),
    defaultValues: {
      name: team?.name ?? '',
      description: team?.description ?? '',
      color: team?.color ?? '',
    },
  });

  const [prevFormResetKey, setPrevFormResetKey] = useState('');
  const formResetKey = open
    ? [team?.id ?? 'new', team?.name, team?.description, team?.color].join('\0')
    : '';
  if (open && formResetKey !== prevFormResetKey) {
    setPrevFormResetKey(formResetKey);
    form.reset({
      name: team?.name ?? '',
      description: team?.description ?? '',
      color: team?.color ?? '',
    });
  } else if (!open && prevFormResetKey !== '') {
    setPrevFormResetKey('');
  }

  const watchedColor = useWatch({ control, name: 'color' });

  const onSubmit = async (data: TeamFormData) => {
    setIsSubmitting(true);
    try {
      const body = {
        name: data.name.trim(),
        description: data.description?.trim() || undefined,
        color: data.color || undefined,
      };

      const response = isEdit
        ? await TeamsApi.update({
            path: { workspaceId, teamId: team!.id },
            body,
          })
        : await TeamsApi.create({
            path: { workspaceId },
            body,
          });

      toast.success(dict.successMessage);
      form.reset();
      onOpenChange(false);
      if (response.data) {
        onSuccess(response.data as Team);
      }
    } catch (err: unknown) {
      console.error('Failed to save team:', err);
      const apiMsg =
        (err as { response?: { data?: { errors?: { name?: string } } } })
          ?.response?.data?.errors?.name === 'teamNameAlreadyExists'
          ? dict.errorNameTaken
          : dict.errorMessage;
      toast.error(apiMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={(next) => {
        void triggerTapHaptic();
        onOpenChange(next);
      }}
    >
      <ResponsiveDialogContent className="sm:max-w-[500px]">
        <ResponsiveDialogHeader>
          <ResponsiveDialogTitle>{dict.title}</ResponsiveDialogTitle>
          <ResponsiveDialogDescription>
            {dict.description}
          </ResponsiveDialogDescription>
        </ResponsiveDialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="team-name">{dict.nameLabel}</Label>
            <Input
              id="team-name"
              type="text"
              placeholder={dict.namePlaceholder}
              autoFocus
              {...form.register('name')}
            />
            {form.formState.errors.name && (
              <p className="text-sm text-destructive">
                {form.formState.errors.name.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="team-description">{dict.descriptionLabel}</Label>
            <Textarea
              id="team-description"
              rows={3}
              placeholder={dict.descriptionPlaceholder}
              {...form.register('description')}
            />
          </div>

          <div className="space-y-2">
            <Label>{dict.colorLabel}</Label>
            <div className="flex flex-wrap gap-2">
              {TEAM_COLOR_PRESETS.map((preset) => {
                const isActive = watchedColor === preset;
                return (
                  <button
                    key={preset}
                    type="button"
                    aria-pressed={isActive}
                    onClick={() => form.setValue('color', preset)}
                    className={`h-8 w-8 rounded-full border-2 transition ${
                      isActive
                        ? 'border-foreground scale-110'
                        : 'border-transparent hover:scale-105'
                    }`}
                    style={{ backgroundColor: preset }}
                  />
                );
              })}
              <button
                type="button"
                aria-pressed={!watchedColor}
                onClick={() => form.setValue('color', '')}
                className={`h-8 rounded-full border px-2 text-xs ${
                  !watchedColor
                    ? 'border-foreground bg-muted'
                    : 'border-input bg-background'
                }`}
              >
                —
              </button>
            </div>
          </div>

          <ResponsiveDialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              {dict.cancel}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? dict.submittingLabel : dict.submitLabel}
            </Button>
          </ResponsiveDialogFooter>
        </form>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}
